import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, BookOpen, Plus, Clock, XCircle, PlayCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Avatar } from "@/components/common";
import { Stars } from "@/components/Stars";

function StatusBadge({ status }) {
  if (status === "pending")
    return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-amber/15 text-amber rounded-full px-2 py-0.5"><Clock size={11} /> In review</span>;
  if (status === "rejected")
    return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-destructive/15 text-destructive rounded-full px-2 py-0.5"><XCircle size={11} /> Declined</span>;
  return null;
}

function CourseCard({ course, showStatus }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/courses/${course.course_id}`)}
      data-testid={`course-card-${course.course_id}`}
      className="group text-left border border-border bg-card rounded-xl overflow-hidden hover:border-cobalt transition-colors flex flex-col"
    >
      <div className="relative aspect-[16/10] overflow-hidden border-b border-border bg-secondary">
        {course.cover_image && (
          <img src={course.cover_image} alt={course.title} className="w-full h-full object-cover warm-img" />
        )}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <span className={`text-[10px] font-mono uppercase tracking-wider rounded-full px-2 py-1 ${
            course.is_paid ? "bg-cobalt text-white" : "bg-background/90 border border-border"}`}>
            {course.is_paid ? `$${course.price}` : "Free"}
          </span>
          {showStatus && <StatusBadge status={course.status} />}
        </div>
        {course.is_paid && !course.unlocked && (
          <div className="absolute top-3 right-3 bg-background/90 border border-border rounded-md p-1.5"><Lock size={13} /></div>
        )}
        {course.purchased && (
          <div className="absolute top-3 right-3 bg-cobalt text-white rounded-md p-1.5"><CheckCircle2 size={13} /></div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        {course.categories?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {course.categories.slice(0, 2).map((c) => (
              <span key={c} className="text-[10px] text-cobalt bg-secondary rounded-full px-2 py-0.5">{c}</span>
            ))}
          </div>
        )}
        <h3 className="font-serif text-xl font-bold leading-tight group-hover:text-cobalt transition-colors">{course.title}</h3>
        {course.rating_count > 0 && (
          <div className="mt-1.5 flex items-center gap-1.5">
            <Stars value={course.rating_avg} size={13} />
            <span className="text-xs text-muted-foreground font-mono">{course.rating_avg} ({course.rating_count})</span>
          </div>
        )}
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">{course.description}</p>
        {course.author && (
          <div className="mt-3 flex items-center gap-2">
            <Avatar src={course.author.picture} name={course.author.name} size={22} />
            <span className="text-xs text-muted-foreground">by {course.author.name}</span>
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
            <BookOpen size={13} /> {course.lesson_count} lessons
          </span>
          <span className="text-xs font-mono text-muted-foreground">{course.progress_pct}%</span>
        </div>
      </div>
    </button>
  );
}

function ContinueLearning({ courses }) {
  const navigate = useNavigate();
  const inProgress = courses.filter((c) => c.completed_count > 0 && c.completed_count < c.lesson_count);
  if (inProgress.length === 0) return null;
  return (
    <div data-testid="continue-learning">
      <div className="eyebrow text-gold mb-3 flex items-center gap-2"><PlayCircle size={14} /> Pick up where you left off</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {inProgress.slice(0, 3).map((c) => {
          const next = (c.lessons || []).find((l) => !l.completed);
          return (
            <button key={c.course_id} onClick={() => navigate(`/courses/${c.course_id}`)}
              data-testid={`continue-course-${c.course_id}`}
              className="group text-left border border-gold/30 bg-secondary/30 rounded-xl p-4 hover:border-gold transition-colors flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary shrink-0 border border-border">
                  {c.cover_image && <img src={c.cover_image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold leading-tight line-clamp-1 group-hover:text-cobalt transition-colors">{c.title}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {next ? <>Next: {next.title}</> : "Finish the last lesson"}
                  </p>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[11px] font-mono text-muted-foreground mb-1">
                  <span>{c.completed_count}/{c.lesson_count} done</span>
                  <span>{c.progress_pct}%</span>
                </div>
                <div className="h-[3px] bg-border rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${c.progress_pct}%` }} /></div>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-gold self-start">
                Continue <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Courses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [options, setOptions] = useState([]);
  const [category, setCategory] = useState("");
  const [mine, setMine] = useState(false);
  const [sort, setSort] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (mine) params.mine = true;
    if (category) params.category = category;
    if (sort) params.sort = sort;
    const res = await api.get("/courses", { params });
    setCourses(res.data);
    setLoading(false);
  }, [mine, category, sort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Learn & Teach" title="Courses"
        subtitle="Learn from the community — and share what you know."
        action={
          <button onClick={() => navigate("/courses/new")} data-testid="upload-course-button"
            className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-gold-hover transition-colors shrink-0">
            <Plus size={16} /> Upload a course
          </button>
        } />

      {!mine && !loading && <ContinueLearning courses={courses} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-md border border-border overflow-hidden">
          <button onClick={() => setMine(false)} data-testid="tab-all-courses"
            className={`px-4 py-2 text-sm transition-colors ${!mine ? "bg-cobalt text-white" : "hover:bg-secondary/60"}`}>All courses</button>
          <button onClick={() => setMine(true)} data-testid="tab-my-courses"
            className={`px-4 py-2 text-sm transition-colors ${mine ? "bg-cobalt text-white" : "hover:bg-secondary/60"}`}>My uploads</button>
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="category-filter"
          className="bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All categories</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-filter"
          className="bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none">
          <option value="">Newest</option>
          <option value="top">Top rated</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>
      ) : courses.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl text-muted-foreground">
          {mine ? "You haven't uploaded any courses yet." : "No courses found."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map((c) => <CourseCard key={c.course_id} course={c} showStatus={mine} />)}
        </div>
      )}
    </div>
  );
}
